import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  {
    /*
     * Accès externe par lien sécurisé (0044). PUBLIC au sens du routeur — la
     * page ne dépend d'aucune session collaborateur et s'affiche sans la coque
     * de l'application — mais rien n'y est ouvert : tout passe par le cookie de
     * session externe, et l'API borne chaque appel au projet de l'invitation.
     */
    path: '/externe/:token',
    name: 'externe',
    component: () => import('@/pages/ExternalProjectPage.vue'),
    meta: { title: 'Accès projet', public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: 'Connexion', public: true },
  },
  {
    path: '/',
    name: 'accueil',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { title: 'Accueil' },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/NotificationsPage.vue'),
    meta: { title: 'Notifications' },
  },
  {
    path: '/projets',
    name: 'projets',
    component: () => import('@/pages/ProjetsPage.vue'),
    meta: { title: 'Projets' },
  },
  {
    /*
     * Les MISSIONS CLIENT (0061). Écran séparé de « Projets », même détail :
     * une mission EST un projet — actions, tâches, budget, réunions — et
     * `/projets/:id` l'ouvre. Ce qui diffère, c'est la liste : regroupée par
     * client, parce que la question posée est « où en est-on chez un tel ».
     */
    path: '/missions',
    name: 'missions',
    component: () => import('@/pages/MissionsPage.vue'),
    meta: { title: 'Missions client' },
  },
  {
    path: '/projets/:id',
    name: 'projet-detail',
    component: () => import('@/pages/ProjetDetailPage.vue'),
    meta: { title: 'Projet' },
  },
  {
    path: '/actions',
    name: 'actions',
    component: () => import('@/pages/ActionsPage.vue'),
    meta: { title: 'Actions' },
  },
  {
    path: '/taches',
    name: 'taches',
    component: () => import('@/pages/TachesPage.vue'),
    meta: { title: 'Tâches' },
  },
  {
    path: '/plan-de-charge',
    name: 'plan-de-charge',
    component: () => import('@/pages/PlanChargePage.vue'),
    meta: { title: 'Plan de charge' },
  },
  {
    /*
     * L'ANCIENNE ADRESSE D'UN DOSSIER, conservée en simple RENVOI.
     *
     * Le module « Mise en production » a été retiré le 2026-09-01 et son écran
     * de liste n'existe plus. Mais les notifications DÉJÀ ENVOYÉES portent
     * `/mise-en-production/<id>` dans leur lien, et elles vivent en base : les
     * laisser tomber sur « Introuvable » aurait cassé l'historique de tout le
     * monde pour économiser trois lignes. Le renvoi ne coûte rien et disparaîtra
     * quand ces notifications auront été classées.
     */
    path: '/mise-en-production/:id',
    redirect: (to) => `/developpement/mises-en-production/${to.params.id as string}`,
  },
  {
    // Espace du testeur — ouvert par l'étiquette « testeur au sein du projet »
    // (0038), sans condition de rôle. L'API applique la même règle.
    path: '/tests',
    name: 'tests',
    component: () => import('@/pages/TestsPage.vue'),
    meta: { title: 'Tests et améliorations' },
  },
  {
    /*
     * Module 5 — espace de développement (0052). Ouvert par la FONCTION
     * développeur, et par l'étiquette testeur d'un projet, qui n'y trouve que
     * l'onglet des remontées. L'API tranche pour de bon ; ici on n'ajoute pas
     * de garde de route, parce que la condition n'est pas un rôle et que le
     * routeur ne connaît que ceux-là.
     *
     * MISE À JOUR DU 2026-09-01. Le testeur n'entre plus (`canOpen` vaut
     * `canView`), et c'est L'ÉCRAN qui referme la porte : il attend de connaître
     * la position de l'appelant, puis affiche un refus explicite au lieu de se
     * remplir. Sans cela, retirer l'entrée de menu n'empêchait rien — une
     * adresse collée suffisait.
     */
    path: '/developpement',
    name: 'developpement',
    component: () => import('@/pages/DeveloppementPage.vue'),
    meta: { title: 'Développement' },
  },
  {
    /*
     * UN DOCUMENT DU DÉPÔT, EN FENÊTRE À PART — ouverte par un double-clic dans
     * l'arborescence. `plein` : réservée aux comptes connectés comme toute page,
     * mais rendue SANS le menu de l'outil — on y vient écrire, pas naviguer.
     * Les droits sont ceux des routes d'API qu'elle appelle.
     */
    path: '/documentation/editeur',
    name: 'documentation-editeur',
    component: () => import('@/pages/EditeurDocumentPage.vue'),
    meta: { title: 'Document', plein: true },
  },
  {
    path: '/developpement/applications/:id',
    name: 'developpement-application',
    component: () => import('@/pages/ApplicationDetailPage.vue'),
    meta: { title: 'Application' },
  },
  {
    /*
     * Le DOSSIER DE LIVRAISON (Tests · Recette · Run), sous le module
     * Développement depuis le 2026-09-01. Le chemin dit à qui il appartient :
     * ce n'est plus un module à part, c'est un écran du développeur.
     */
    path: '/developpement/mises-en-production/:id',
    name: 'developpement-mise-en-production',
    component: () => import('@/pages/MiseEnProductionDetailPage.vue'),
    meta: { title: 'Mise en production' },
  },
  {
    path: '/developpement/remontees/:id',
    name: 'developpement-remontee',
    component: () => import('@/pages/RemonteeDetailPage.vue'),
    meta: { title: 'Remontée' },
  },
  {
    path: '/plans',
    name: 'plans',
    component: () => import('@/pages/PlansPage.vue'),
    meta: { title: "Plans d'actions" },
  },
  {
    path: '/plans/:id',
    name: 'plan-detail',
    component: () => import('@/pages/PlanDetailPage.vue'),
    meta: { title: "Plan d'actions" },
  },
  {
    path: '/ideation',
    name: 'ideation',
    component: () => import('@/pages/IdeationPage.vue'),
    meta: { title: 'Idéation' },
  },
  {
    path: '/aide',
    name: 'aide',
    component: () => import('@/pages/AidePage.vue'),
    meta: { title: 'Aide' },
  },
  {
    path: '/mon-espace',
    name: 'mon-espace',
    component: () => import('@/pages/MonEspacePage.vue'),
    meta: { title: 'Mon espace' },
  },
  {
    path: '/decouverte',
    name: 'decouverte',
    component: () => import('@/pages/DecouvertePage.vue'),
    meta: { title: "Découvrir l'outil" },
  },
  {
    path: '/portefeuille',
    name: 'portefeuille',
    component: () => import('@/pages/PortefeuillePage.vue'),
    meta: { title: 'Portefeuille', direction: true },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/pages/JournalAuditPage.vue'),
    meta: { title: "Journal d'audit", direction: true },
  },
  {
    path: '/administration',
    name: 'administration',
    component: () => import('@/pages/AdministrationPage.vue'),
    // « Administration (direction) » — CDC p.12. L'API refuse déjà (403) : ce
    // garde évite d'afficher une page qui ne se remplirait jamais.
    meta: { title: 'Administration', direction: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'Introuvable' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  if (!auth.ready) await auth.init();

  if (to.meta.public) {
    // Déjà connecté : pas la peine de rester sur /login.
    if (to.name === 'login' && auth.isAuthenticated) return { path: '/' };
    return true;
  }
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  // Réservé à la direction (l'API tranche pour de bon ; ici on évite d'ouvrir
  // une page vide à qui n'y a pas droit).
  if (to.meta.direction && !auth.isDirection) {
    return { path: '/' };
  }
  return true;
});

router.afterEach((to) => {
  const title = (to.meta.title as string | undefined) ?? '';
  document.title = title ? `${title} — Plans d'actions` : "Plans d'actions — IT Solutions";
});
